import React, { useState, useEffect } from 'react';
import { Modal, Card, Checkbox, Badge, Tooltip, Pagination, Row, Col, Button, Input, Switch, Tag } from 'antd';
import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { startCase, debounce } from 'lodash';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import moment from 'moment';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import { useProjectContext } from '../../../../../core/components/projects/useProjectContext';
import { urgencyColors } from '../../configs/task.config';
import useResponsiveTask from '../../hooks/useResponsiveTask';
import { space } from '../../configs/designTokens';
import '../../styles/index.scss';

const AddExistingTaskModal = ({ visible, onCancel, currentUser, currentProject, updateProject, refetchProject, onAddTasks, updateProjectLoading }) => {
  const { width: windowWidth, isMobile, isDesktop } = useResponsiveTask();
  const { fetchMilestones, milestones, milestonesLoading } = useProjectContext();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [selectedTasksMap, setSelectedTasksMap] = useState({});  // Store full task objects by ID
  const [selectedMilestoneIds, setSelectedMilestoneIds] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [showMyTasks, setShowMyTasks] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [affectedUsers, setAffectedUsers] = useState([]);

  const { fetchTasksPaginated, tasksConnectionData, tasksLoading } = useTaskContext();

  useEffect(() => {
    if (visible) {
      handleFetchTasks();
      if (currentProject?.id) {
        fetchMilestones({
          variables: {
            where: { project: { id: currentProject.id } },
            orderBy: 'dueDate_ASC',
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, pagination.current, pagination.pageSize, showMyTasks, searchText]);

  const handleFetchTasks = () => {
    const skip = (pagination.current - 1) * pagination.pageSize;

    const currentProjectTaskIds = currentProject?.tasks?.map(task => task.id) || [];

    fetchTasksPaginated({
      variables: {
        first: pagination.pageSize,
        skip,
        orderBy: 'updatedAt_DESC',
        where: {
          AND: [
            { name_contains: searchText },
            ...(currentProjectTaskIds.length > 0 ? [{ id_not_in: currentProjectTaskIds }] : []),
            { project: null },
            ...(showMyTasks
              ? [
                  {
                    OR: [
                      { assigneeUser: { id: currentUser.id } },
                      { assigneeUsers_some: { id: currentUser.id } },
                      { createdBy: { id: currentUser.id } },
                      { followers_some: { id: currentUser.id } },
                      { collaborators_some: { id: currentUser.id } },
                    ],
                  },
                ]
              : []),
            {
              OR: [
                {
                  createdBy: {
                    roles_some: {
                      org: {
                        OR: [
                          { parentOrg_some: { id: currentUser.role.org.id } },
                          { id: currentUser.role.org.id },
                        ],
                      },
                    },
                  },
                },
              ],
            },
          ],
        },
      },
    });
  };

  const handleSearchChange = debounce((value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
  }, 0);

  useEffect(() => {
    if (tasksConnectionData && tasksConnectionData.tasksConnection) {
      setPagination((prev) => ({
        ...prev,
        total: tasksConnectionData.full?.aggregate?.count || 0,
      }));
    }
  }, [tasksConnectionData]);

  const handleTaskSelect = (task) => {
    setSelectedTaskIds((prev) => {
      if (prev.includes(task.id)) {
        setSelectedTasksMap((prevMap) => {
          const newMap = { ...prevMap };
          delete newMap[task.id];
          return newMap;
        });
        return prev.filter((id) => id !== task.id);
      } else {
        setSelectedTasksMap((prevMap) => ({
          ...prevMap,
          [task.id]: task,
        }));
        return [...prev, task.id];
      }
    });
  };

  const handlePaginationChange = (page, pageSize) => {
    setPagination({
      ...pagination,
      current: page,
      pageSize: pageSize || pagination.pageSize,
    });
  };

  const handleCancel = () => {
    setCurrentStep(1);
    setSelectedTaskIds([]);
    setSelectedTasksMap({});
    setSelectedMilestoneIds([]);
    setPagination({ current: 1, pageSize: 10, total: 0 });
    setShowWarningModal(false);
    setAffectedUsers([]);
    onCancel();
  };

  const handleMilestoneSelect = (milestoneId) => {
    setSelectedMilestoneIds((prev) =>
      prev.includes(milestoneId) ? prev.filter((id) => id !== milestoneId) : [...prev, milestoneId]
    );
  };

  const handleNext = () => {
    if (selectedTaskIds.length === 0) {
      return;
    }
    setCurrentStep(2);
  };

  const tasks = tasksConnectionData?.tasksConnection?.edges?.map((edge) => edge.node) || [];

  const selectedTasks = Object.values(selectedTasksMap);

  const checkForNonMemberAssignees = () => {
    const projectMemberIds = currentProject?.team?.map(member => member.id) || [];
    const projectOwnerId = currentProject?.owner?.id;
    const allProjectUserIds = [...projectMemberIds, projectOwnerId].filter(Boolean);

    const nonMembers = [];

    selectedTasks.forEach(task => {
      if (task.assigneeUsers && task.assigneeUsers.length > 0) {
        task.assigneeUsers.forEach(assignee => {
          if (!allProjectUserIds.includes(assignee.id)) {
            if (!nonMembers.find(u => u.id === assignee.id)) {
              nonMembers.push({
                id: assignee.id,
                name: assignee.name || assignee.username,
                avatar: assignee.avatar,
              });
            }
          }
        });
      }
    });

    return nonMembers;
  };

  const handleAddTasksClick = () => {
    console.log('handleAddTasksClick called');
    console.log('selectedTaskIds:', selectedTaskIds);
    console.log('selectedTasks:', selectedTasks);

    const nonMemberAssignees = checkForNonMemberAssignees();
    console.log('nonMemberAssignees:', nonMemberAssignees);

    if (nonMemberAssignees.length > 0) {
      setAffectedUsers(nonMemberAssignees);
      setShowWarningModal(true);
    } else {
      handleConfirmAddTasks();
    }
  };

  const handleConfirmAddTasks = async () => {
    console.log('handleConfirmAddTasks called');
    try {
      await onAddTasks(selectedTaskIds, selectedTasks, selectedMilestoneIds);
      setShowWarningModal(false);
      handleCancel();
    } catch (error) {
      console.error('Error in handleConfirmAddTasks:', error);
    }
  };

  return (
    <Modal
      className="task-add-modal"
      title={`Add Existing Tasks - Step ${currentStep} of 2`}
      open={visible}
      onCancel={handleCancel}
      width={windowWidth < 768 ? '95%' : 900}
      centered
      bodyStyle={{ padding: windowWidth < 768 ? space[4] : space[6] }}
      footer={
        <div className="task-add-modal__footer">
          <Text className="task-add-modal__footer-count">
            {selectedTaskIds.length} task(s) selected
          </Text>
          <div className="task-add-modal__footer-actions">
            <Button onClick={handleCancel} className="task-modal-btn">
              Cancel
            </Button>
            {currentStep === 1 && (
              <Button
                type="primary"
                onClick={handleNext}
                disabled={selectedTaskIds.length === 0}
                className="task-modal-btn task-modal-btn--primary"
              >
                Next
              </Button>
            )}
            {currentStep === 2 && (
              <>
                <Button
                  onClick={() => setCurrentStep(1)}
                  disabled={updateProjectLoading}
                  className="task-modal-btn"
                >
                  Back
                </Button>
                <Button
                  type="primary"
                  loading={updateProjectLoading}
                  disabled={updateProjectLoading}
                  onClick={handleAddTasksClick}
                  className="task-modal-btn task-modal-btn--primary"
                >
                  Add Tasks
                </Button>
              </>
            )}
          </div>
        </div>
      }
    >
      {currentStep === 1 && (
        <div>
          <Text className="task-add-modal__description">
            Select the tasks you want to add to this project
          </Text>

          {/* Filters */}
          <div className="task-add-modal__filters">
            <div className="task-add-modal__filter-row">
              <div className="task-add-modal__filter-toggle">
                <Text>My Tasks</Text>
                <Switch
                  checked={showMyTasks}
                  onChange={(checked) => {
                    setShowMyTasks(checked);
                    setPagination({ ...pagination, current: 1 });
                  }}
                />
              </div>
              <div className="task-add-modal__filter-actions">
                <Button
                  type="text"
                  onClick={() => {
                    setSelectedTaskIds([]);
                    setSelectedTasksMap({});
                  }}
                  disabled={selectedTaskIds.length === 0}
                  className="task-add-modal__clear-btn"
                >
                  Clear Selection
                </Button>
                <Input
                  placeholder="Search tasks"
                  value={searchText}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="task-add-modal__search"
                  allowClear
                />
              </div>
            </div>
          </div>

          <div className="task-add-modal__task-list">
            {tasks.map((taskItem) => (
              <Card
                key={taskItem.id}
                hoverable
                onClick={() => handleTaskSelect(taskItem)}
                className={`task-add-modal__task-card ${selectedTaskIds.includes(taskItem.id) ? 'task-add-modal__task-card--selected' : ''}`}
              >
                <div className="task-add-modal__task-content">
                  <Checkbox
                    checked={selectedTaskIds.includes(taskItem.id)}
                    onChange={() => handleTaskSelect(taskItem)}
                    onClick={(e) => e.stopPropagation()}
                  />

                  <div className="task-add-modal__task-main">
                    <div className="task-add-modal__task-row">
                      <div className="task-add-modal__task-info">
                        <div className="task-add-modal__task-header">
                          <Tooltip title={taskItem.urgency ? `URGENCY: ${taskItem.urgency}` : 'NO URGENCY'}>
                            <Badge
                              dot
                              color={urgencyColors[taskItem.urgency] || 'white'}
                              className="task-custom-badge"
                            />
                          </Tooltip>
                          <Text strong className="task-add-modal__task-name">
                            {taskItem.name}
                          </Text>
                        </div>
                        {taskItem.requestType && (
                          <Text className="task-add-modal__task-type">
                            {taskItem.requestType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                          </Text>
                        )}
                      </div>

                      <div className="task-add-modal__task-meta">
                        {taskItem.dueDate && (
                          <div className="task-add-modal__task-date">
                            <Text className="task-add-modal__task-date-label">Due:</Text>
                            <Text className="task-add-modal__task-date-value">
                              {moment(taskItem.dueDate).format('MMM D, YYYY')}
                            </Text>
                          </div>
                        )}

                        {taskItem.assigneeUsers && taskItem.assigneeUsers.length > 0 && (
                          <div className="task-add-modal__task-assignees">
                            {taskItem.assigneeUsers.slice(0, 3).map((user, idx) => (
                              <Tooltip
                                key={user.id || idx}
                                title={startCase(user.name || user.username)}
                              >
                                <span>
                                  <AvatarS3
                                    imgKey={user.avatar && user.avatar.key}
                                    username={user.name || user.username}
                                    avatarProps={{ size: 24 }}
                                  />
                                </span>
                              </Tooltip>
                            ))}
                            {taskItem.assigneeUsers.length > 3 && (
                              <Text className="task-add-modal__task-assignee-overflow">
                                +{taskItem.assigneeUsers.length - 3}
                              </Text>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {tasks.length === 0 && !tasksLoading && (
            <div className="task-add-modal__empty">
              No tasks available
            </div>
          )}

          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={handlePaginationChange}
            onShowSizeChange={handlePaginationChange}
            showSizeChanger={windowWidth >= 768}
            simple={windowWidth < 768}
            size={windowWidth < 768 ? 'small' : 'default'}
            className="task-mt--md"
          />
        </div>
      )}

      {currentStep === 2 && (
        <div>
          <Text className="task-add-modal__section-title">
            Assign to Milestone
          </Text>

          <div className="task-add-modal__selected-tasks">
            <Text className="task-add-modal__selected-label">
              Selected Tasks ({selectedTasks.length})
            </Text>
            <Card className="task-add-modal__selected-card">
              <div className="task-add-modal__selected-tags">
                {selectedTasks.map((task) => (
                  <Tag key={task.id} color="blue" className="task-add-modal__selected-tag">
                    {task.name}
                  </Tag>
                ))}
              </div>
            </Card>
          </div>

          <div>
            <Text className="task-add-modal__selected-label">
              Select Milestones (Optional)
            </Text>

            <div className="task-add-modal__milestone-list">
              {milestonesLoading && (
                <Card className="task-add-modal__milestone-card task-add-modal__milestone-card--empty">
                  <Text>Loading milestones...</Text>
                </Card>
              )}

              {!milestonesLoading && (!milestones || milestones.length === 0) && (
                <Card className="task-add-modal__milestone-card task-add-modal__milestone-card--empty">
                  <Text>No milestones available. Add a milestone first or skip this step.</Text>
                </Card>
              )}

              {!milestonesLoading && milestones?.map((milestone) => (
                <Card
                  key={milestone.id}
                  hoverable
                  onClick={() => handleMilestoneSelect(milestone.id)}
                  className={`task-add-modal__milestone-card ${selectedMilestoneIds.includes(milestone.id) ? 'task-add-modal__milestone-card--selected' : ''}`}
                >
                  <div className="task-add-modal__milestone-content">
                    <Checkbox
                      checked={selectedMilestoneIds.includes(milestone.id)}
                      onChange={() => handleMilestoneSelect(milestone.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Text strong className="task-add-modal__milestone-name">
                      {milestone.name}
                    </Text>
                  </div>
                </Card>
              ))}
            </div>

            <Text className="task-add-modal__milestone-hint">
              You can add tasks without selecting a milestone
            </Text>
          </div>
        </div>
      )}

      {/* Warning Modal */}
      <Modal
        className="task-add-modal"
        title={
          <div className="task-add-modal__warning-title">
            <ExclamationCircleOutlined className="task-add-modal__warning-icon" />
            <span>Assignee Conversion Warning</span>
          </div>
        }
        open={showWarningModal}
        onCancel={() => setShowWarningModal(false)}
        width={windowWidth < 768 ? '95%' : 600}
        centered
        bodyStyle={{ padding: windowWidth < 768 ? space[4] : space[6] }}
        footer={
          <div className="task-add-modal__warning-footer">
            <Button
              onClick={() => setShowWarningModal(false)}
              disabled={updateProjectLoading}
              className="task-modal-btn"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={updateProjectLoading}
              disabled={updateProjectLoading}
              onClick={handleConfirmAddTasks}
              className="task-modal-btn task-add-modal__warning-btn"
            >
              Confirm & Add Tasks
            </Button>
          </div>
        }
      >
        <div>
          <Text className="task-add-modal__warning-text">
            The following users are assigned to the selected tasks but are not members or owners of this project:
          </Text>

          <Card className="task-add-modal__warning-card">
            <div className="task-add-modal__warning-users">
              {affectedUsers.map((user) => (
                <div key={user.id} className="task-add-modal__warning-user">
                  <AvatarS3
                    imgKey={user.avatar && user.avatar.key}
                    username={user.name}
                    avatarProps={{ size: 24 }}
                  />
                  <Text>{startCase(user.name)}</Text>
                </div>
              ))}
            </div>
          </Card>

          <Text className="task-add-modal__warning-note">
            When you proceed, these users will be converted to <strong>collaborators</strong> on the tasks instead of assignees. They will still be able to view and work on the tasks.
          </Text>
        </div>
      </Modal>
    </Modal>
  );
};

export default AddExistingTaskModal;
